import useSWR from 'swr';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import {
  Accordion,
  Button,
  Headline,
  Section,
  PageLoader,
} from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../../mobx-utils';
import { parseHtml } from '../../core/utils';
import { useStore } from '../../core/store';
import { useTranslation } from '../../core/utils/translations';

import { ActivityCarousel } from '../../components/activity-carousel';
import { ErrorPage } from '../../components/error-page';
import { PageWrapper } from '../../components/page-wrapper';
import { dateFormatter } from '../../utils/date-formatter';
import { useExcursionsContext } from './index';
import { urls } from '../../urls';

export const ExcursionsDetails = observer(() => {
  const { t } = useTranslation(['activities-page', 'error-pages']);

  const { booking } = useStore().BookingStore;

  const { code } = useParams();

  const daysOfWeek = {
    Montag: 'monday',
    Dienstag: 'tuesday',
    Mittwoch: 'wednesday',
    Donnerstag: 'thursday',
    Freitag: 'friday',
    Samstag: 'saturday',
    Sonntag: 'sunday',
  };

  const { data, error, isLoading } = useSWR(
    '/rms/api/excursions/' + code,
    (url) =>
      fetch(url)
        .then((res) => res.json())
        .catch((err) => console.error(err)),
  );

  if (isLoading || booking.status === LoadableStatus.loading) {
    return (
      <PageLoader variant="absolute" info="Buchungsformular wird geladen..." />
    );
  }

  if (!data?.id || error) {
    return (
      <PageWrapper>
        <ErrorPage
          body={t('error-pages:activities-fetch-error-body-text')}
          buttonText={t('error-pages:activities-fetch-error-back-button-text')}
          icon={'icon-warning'}
          link={urls.home}
          title={t('error-pages:activities-fetch-error-title-text')}
        />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ActivityCarousel slides={data?.images.map((image) => image.thumb)} />

      <Section>
        {/* TODO: extract component to display excursion */}
        {data?.title ? (
          <Headline
            title={data?.title}
            variant="primary"
            className="text-semibold mb-5"
          />
        ) : null}

        {data?.labels ? (
          <Accordion
            headline={t('activities-page:activity-categories-text')}
            content={data?.labels.map((label, index) => (
              <div key={index} className="mb-2 mr-2 inline-block">
                <div className="flex cursor-pointer items-center rounded-3xl px-3 py-2 shadow bg-[#e3f0f0] text-brand-1">
                  <span className="text-[10px] font-semibold uppercase">
                    {label}
                  </span>
                </div>
              </div>
            ))}
          />
        ) : null}

        {data?.highlights ? (
          <Accordion
            headline={t('activities-page:activity-highlights-text')}
            content={parseHtml(data?.highlights)}
          />
        ) : null}

        <Accordion
          headline={t('activities-page:activity-prices-text')}
          content={
            <>
              {data?.adultPrice ? (
                <div className="mb-1">
                  <span className="font-semibold pr-2">{`${t(
                    'activities-page:activity-label-adult-price',
                  )}:`}</span>
                  <span>{data?.adultPrice} €</span>
                </div>
              ) : null}
              {data?.childPrice !== null ? (
                <div className="mb-1">
                  <span className="font-semibold pr-2">
                    {`${t('activities-page:activity-label-child-price')} ${
                      data?.childMaxAge
                    }:`}
                  </span>
                  {data?.childPrice === 0 ? (
                    <span>{`${t(
                      'activities-page:activity-free-of-charge',
                    )}`}</span>
                  ) : (
                    <span>{data?.childPrice} €</span>
                  )}
                </div>
              ) : (
                <div className="mb-1">
                  <span className="font-semibold pr-2">
                    {`${t('activities-page:activity-label-child-price')} ${
                      data?.childMaxAge
                    }:`}
                  </span>
                  <span>{`${t(
                    'activities-page:activity-not-available',
                  )}`}</span>
                </div>
              )}
              {data?.infantPrice !== null ? (
                <div className="mb-1">
                  <span className="font-semibold pr-2">
                    {' '}
                    {`${t('activities-page:activity-label-infant-price')} ${
                      data?.infantMaxAge
                    }:`}
                  </span>
                  {data?.infantPrice === 0 ? (
                    <span>{`${t(
                      'activities-page:activity-free-of-charge',
                    )}`}</span>
                  ) : (
                    <span>{data?.infantPrice} €</span>
                  )}
                </div>
              ) : (
                <div className="mb-1">
                  <span className="font-semibold pr-2">
                    {`${t('activities-page:activity-label-infant-price')} ${
                      data?.infantMaxAge
                    }:`}
                  </span>
                  <span>{`${t(
                    'activities-page:activity-not-available',
                  )}`}</span>
                </div>
              )}
            </>
          }
        />

        {data?.pickup ? (
          <Accordion
            headline={t('activities-page:activity-info-pickup')}
            content={parseHtml(data?.pickup)}
          />
        ) : null}

        {data?.duration ? (
          <Accordion
            headline={t('activities-page:activity-info-duration')}
            content={
              <>
                <span>{data?.duration}</span>
              </>
            }
          />
        ) : null}

        {data?.flexibility ? (
          <Accordion
            headline={t('activities-page:activity-info-flexibility')}
            content={parseHtml(data?.flexibility)}
          />
        ) : null}

        {data?.description ? (
          <Accordion
            headline={t('activities-page:activity-info-description')}
            content={parseHtml(data?.description)}
          />
        ) : null}

        {data?.inclusives ? (
          <Accordion
            headline={t('activities-page:activity-info-inclusives')}
            content={parseHtml(data?.inclusives)}
          />
        ) : null}

        {data?.importantInformation ? (
          <Accordion
            headline={t('activities-page:activity-info-important')}
            content={parseHtml(data?.importantInformation)}
          />
        ) : null}

        {data?.dateFrom && data?.dateTo && data?.days ? (
          <Accordion
            defaultOpened
            headline={t('activities-page:activity-info-opening-hours')}
            content={
              <>
                {data?.dateFrom || data?.dateTo ? (
                  <div className="text-sm mb-2">
                    <span className="pr-1 font-semibold">
                      {t('activities-page:activity-info-date')}:
                    </span>
                    <span className="text-sm">{`${dateFormatter(
                      data?.dateFrom,
                    )} - ${dateFormatter(data?.dateTo)}`}</span>
                  </div>
                ) : null}
                <div className="flex flex-col text-sm">
                  {Object.entries(daysOfWeek).map(([label, day], index) => (
                    <div className="flex items-center mb-1" key={index}>
                      <span className="font-semibold pr-1 w-24">{label}:</span>
                      {data?.days[day] ? (
                        <span className="icon icon-check text-xs text-brand-2">
                          {' '}
                        </span>
                      ) : (
                        <span className="icon icon-close text-red-600"> </span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            }
          />
        ) : null}

        <BookingForm excursion={data} />
      </Section>
    </PageWrapper>
  );
});

export default ExcursionsDetails;

// TODO: extract display logic to component

export function BookingForm({ excursion }) {
  const { state } = useExcursionsContext();
  const [, setBookingForm] = state;

  const { t } = useTranslation('activities-page');

  const [price, setPrice] = useState(0);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      adults: 1,
      children: 0,
      infants: 0,
      excursion_code: excursion.excursionCode,
      excursion_date: new Date(),
      booking_date: new Date(),
      client_url: window.location.origin,

      ...useBlankBookingData(),
    },
  });

  const subscription = watch(['adults', 'children', 'infants']);

  const availableDate = (value) => {
    const enteredDate = new Date(value);
    const enteredDateTimestamp = enteredDate.getTime();
    const dayIndex = enteredDate.getDay();
    const dayNames = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];

    const availableDay = excursion?.days[dayNames[dayIndex]];
    if (
      availableDay &&
      enteredDateTimestamp >= new Date(excursion.bookableFrom).getTime() &&
      enteredDateTimestamp <= new Date(excursion.bookableTo).getTime()
    ) {
      return true;
    }

    return `${t('activity-error-message-date')}`;
  };

  function onSubmit(event) {
    event.preventDefault();
    handleSubmit((data) => {
      setBookingForm({
        ...data,
        total_price: parseInt(price),
        adults: parseInt(data.adults),
        children: parseInt(data.children),
        infants: parseInt(data.infants),
      });
      navigate('buchen');
    })();
  }

  useEffect(() => {
    const [adults, children, infants] = subscription;

    setPrice(
      adults * excursion.adultPrice +
        children * excursion.childPrice +
        infants * excursion.infantPrice,
    );
  }, [subscription]);

  return (
    <form onSubmit={onSubmit} className="rounded-md p-4 bg-brand-1 mt-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
        <div>
          <label htmlFor="client_email" className="text-xs text-white">
            {`${t('activity-label-user-email')}:`}
          </label>
          <input
            className="w-full rounded-md"
            type="email"
            {...register('client_email', {
              required: `${t('activity-error-message-email')}`,
            })}
          />
          {errors?.client_email && (
            <div className="text-red-600	 text-sm mt-2">
              {errors.client_email.message}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="client_phone" className="text-xs text-white">
            {`${t('activity-label-user-phone')}:`}
          </label>
          <input
            className="w-full rounded-md"
            type="tel"
            {...register('client_phone', {
              required: `${t('activity-error-message-phone')}`,
            })}
          />
          {errors?.client_phone && (
            <div className="text-red-600	text-sm mt-2">
              {errors.client_phone.message}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="adults" className="text-xs text-white">
            {`${t('activity-label-adults-count')}:`}
          </label>
          <input
            className="w-full rounded-md"
            type="number"
            {...register('adults')}
            min="1"
          />
        </div>
        {excursion?.childPrice !== null ? (
          <div>
            <label htmlFor="children" className="text-xs text-white">
              {excursion?.infantPrice === null
                ? `${t('activity-label-kids-count-range')} ${
                    excursion?.infantMaxAge
                  }-${excursion?.childMaxAge}:`
                : `${t('activity-label-kids-count-to')} ${
                    excursion?.childMaxAge
                  }:`}
            </label>
            <input
              className="w-full rounded-md"
              type="number"
              {...register('children')}
              min="0"
            />
          </div>
        ) : null}

        {excursion?.infantPrice !== null ? (
          <div>
            <label htmlFor="infants" className="text-xs text-white">
              {`${t('activity-label-infants-count')} ${
                excursion?.infantMaxAge
              }:`}
            </label>
            <input
              className="w-full rounded-md"
              type="number"
              {...register('infants')}
              min="0"
            />
          </div>
        ) : null}
        <div>
          <label htmlFor="infants" className="text-xs text-white mb-1 block">
            {`${t('activity-label-excursion-date')}:`}
          </label>
          <input
            className="rounded-md w-full"
            type="date"
            {...register('excursion_date', {
              validate: availableDate,
            })}
            min={excursion.bookableFrom}
            max={excursion.bookableTo}
          />

          {errors.excursion_date && (
            <div className="text-red-600	 text-sm mt-2">
              {errors.excursion_date.message}
            </div>
          )}
        </div>
      </div>
      <div className="text-white  my-4">
        {`${t('preis-text')}:`} {price} €
      </div>
      {/* TODO: currency format */}
      <Button
        variant="secondary"
        className="!text-white sm:w-auto w-full"
        title="Bestätigen"
      />
    </form>
  );
}

function useBlankBookingData() {
  const { booking } = useStore().BookingStore;

  const blacklistedEmailDomains = [
    '@b.holidaycheck.de',
    '@reisen.check24.de',
    '@my.ab-in-den-urlaub.de',
  ];

  const isBlacklisted = (email) => {
    const atIndex = email.indexOf('@');
    if (atIndex !== 1) {
      const domain = email.slice(atIndex);

      return blacklistedEmailDomains.some(
        (blacklistedDomain) => blacklistedDomain === domain,
      );
    }
    return false;
  };

  return {
    blank_booking_id: booking?.data?.id,
    blank_hotel_code: booking?.data?.hotels[0]?.code,
    blank_hotel_name: booking?.data?.hotels[0]?.name,
    client_name: `${booking?.data?.travellers[0]?.firstname} ${booking?.data?.travellers[0]?.lastname}`,
    client_email: isBlacklisted(booking?.data?.client?.email)
      ? ''
      : booking?.data?.client?.email,
    client_phone: booking?.data?.client?.mobileNumber,
  };
}
